import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FolderIcon } from "@heroicons/react/24/solid";
import { useSettings } from "../../../../contexts/settings-context";
import {
  resolveLineCostDisplay,
  resolveLineSalesDisplay,
  parseMoneyAmount,
} from "../utils";
import {
  formatGroupLabel,
  groupDisplayTitle,
} from "../utils/offer-group-hierarchy";
import { itemsInChapter } from "../utils/offer-calc-item-groups";
import { lineAmount } from "../utils/offer-workspace";
import OfferCalculationSummaryPanel from "./calculation-summary-panel";


const lineTypeLabelKey = (lineType) => {
  switch (lineType) {
    case "manual":
      return "offer.calculation.workspace.line_type.manual";
    case "product":
      return "offer.calculation.workspace.line_type.product";
    case "hours":
      return "offer.calculation.workspace.line_type.hours";
    case "calculation":
      return "offer.calculation.workspace.line_type.calculation";
    default:
      return "offer.calculation.workspace.line_type.other";
  }
};

const formatOrDash = (value, formatter) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  if (typeof formatter === "function") return formatter(value);
  return String(value);
};

/**
 * Right context / property panel for line calculation workspace.
 * Visual hierarchy matches Claude property panel; edit flows stay MVP.
 * When a structure group is selected (no line), shows chapter summary.
 */
export default function OfferCalculationContextPanel({
  calculation = null,
  selectedItem = null,
  selectedIndex = null,
  selectedGroup = null,
  chapterItems = null,
  flatGroups = [],
  allItems = [],
  calculationV2Enabled = false,
  legacyAmount = null,
  onClose,
}) {
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();

  const chapterLines = useMemo(() => {
    if (selectedItem || !selectedGroup) return [];
    if (Array.isArray(chapterItems)) return chapterItems;
    return itemsInChapter(allItems, flatGroups, selectedGroup.id);
  }, [selectedItem, selectedGroup, chapterItems, allItems, flatGroups]);

  const chapterTotals = useMemo(() => {
    if (!selectedGroup || selectedItem) return null;
    let sales = 0;
    let cost = 0;
    let hasSalesEstimate = false;
    chapterLines.forEach((item) => {
      const lineSales = resolveLineSalesDisplay(item, {
        v2: calculationV2Enabled,
        legacyAmount: lineAmount(item),
      });
      const lineCost = resolveLineCostDisplay(item, {
        v2: calculationV2Enabled,
      });
      if (lineSales?.amount != null) sales += Number(lineSales.amount) || 0;
      if (lineSales?.estimate) hasSalesEstimate = true;
      if (lineCost != null) cost += Number(lineCost) || 0;
    });
    const margin = sales - cost;
    const marginPct = sales > 0 ? (margin / sales) * 100 : null;
    return {
      lineCount: chapterLines.length,
      sales,
      cost,
      margin,
      marginPct,
      hasSalesEstimate,
    };
  }, [selectedGroup, selectedItem, chapterLines, calculationV2Enabled]);

  const lineSales = selectedItem
    ? resolveLineSalesDisplay(selectedItem, {
        v2: calculationV2Enabled,
        legacyAmount,
      })
    : null;
  const lineCost = selectedItem
    ? resolveLineCostDisplay(selectedItem, { v2: calculationV2Enabled })
    : null;

  const qty = parseMoneyAmount(selectedItem?.quantity);
  const unitCost = parseMoneyAmount(selectedItem?.purchase_price);
  const unitSales =
    parseMoneyAmount(selectedItem?.nested_calculation?.total_amount) ??
    parseMoneyAmount(selectedItem?.sales_price);
  const surcharge = parseMoneyAmount(selectedItem?.surcharge);

  const salesAmount = lineSales?.amount;
  const dg =
    calculationV2Enabled &&
    lineCost != null &&
    salesAmount != null &&
    salesAmount > 0
      ? ((salesAmount - lineCost) / salesAmount) * 100
      : null;

  const missingCost =
    !!selectedItem &&
    (unitCost == null || !(unitCost > 0)) &&
    (lineCost == null || !(lineCost > 0)) &&
    ((salesAmount != null && salesAmount > 0) ||
      (unitSales != null && unitSales > 0));

  const hasOverride =
    !!selectedItem &&
    (selectedItem.manual_override === true ||
      selectedItem.price_override === true ||
      (selectedItem.override_reason &&
        String(selectedItem.override_reason).trim()));

  const title =
    (selectedItem?.description || "").trim() ||
    selectedItem?.product?.displayTitle ||
    selectedItem?.nested_calculation?.title ||
    (selectedItem
      ? t("offer.calculation.workspace.untitled_line", {
          n: (selectedIndex ?? 0) + 1,
        })
      : "");

  const posLabel = selectedItem
    ? t("offer.calculation.workspace.selected_line", {
        n: (selectedIndex ?? 0) + 1,
      })
    : "";
  const typeLabel = selectedItem
    ? t(lineTypeLabelKey(selectedItem.line_type))
    : "";

  const groupTitle = selectedGroup
    ? formatGroupLabel(
        selectedGroup,
        groupDisplayTitle(selectedGroup) || t("offers.groups.untitled")
      )
    : "";

  return (
    <aside
      className="offer-calc-context"
      aria-label={t("offer.calculation.workspace.context_title")}
    >
      <div className="offer-calc-context__chrome">
        <span className="offer-calc-context__eyebrow">
          {selectedGroup && !selectedItem
            ? t("offer.calculation.workspace.chapter_properties")
            : t("offer.calculation.workspace.properties")}
        </span>
        {onClose ? (
          <button
            type="button"
            className="offer-calc-context__close"
            onClick={onClose}
            aria-label={t("offer.calculation.workspace.hide_context")}
          >
            ✕
          </button>
        ) : null}
      </div>

      {!selectedItem && selectedGroup && chapterTotals ? (
        <>
          <div className="offer-calc-context__headline">
            <div className="offer-calc-context__chapter-title">
              <FolderIcon
                className="offer-calc-context__chapter-folder"
                aria-hidden
              />
              <span>{groupTitle}</span>
            </div>
            <div className="offer-calc-context__line-meta">
              {t("offer.calculation.workspace.chapter_lines", {
                count: chapterTotals.lineCount,
              })}
              {selectedGroup.discipline
                ? ` · ${selectedGroup.discipline}`
                : ""}
            </div>
          </div>

          <dl className="offer-calc-context__props">
            <div>
              <dt>{t("offer.calculation.workspace.field.cost")}</dt>
              <dd className="tabular-nums">
                {priceFormatter.format(chapterTotals.cost)}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.chapter_subtotal")}</dt>
              <dd className="tabular-nums offer-calc-context__props-strong">
                {priceFormatter.format(chapterTotals.sales)}
                {chapterTotals.hasSalesEstimate ? (
                  <span className="offer-calc-context__estimate">
                    {" "}
                    ({t("offer.calculation.totals.estimate_hint")})
                  </span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.margin")}</dt>
              <dd
                className={[
                  "tabular-nums",
                  chapterTotals.marginPct != null && chapterTotals.marginPct >= 0
                    ? "is-positive"
                    : "",
                  chapterTotals.marginPct != null && chapterTotals.marginPct < 0
                    ? "is-negative"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {chapterTotals.marginPct != null
                  ? `${chapterTotals.marginPct.toFixed(1)} %`
                  : "—"}
              </dd>
            </div>
          </dl>

          {selectedGroup.comment || selectedGroup.description ? (
            <div className="offer-calc-context__card">
              <div className="offer-calc-context__card-title">
                {t("offers.groups.field_description")}
              </div>
              <div className="offer-calc-context__card-body">
                {selectedGroup.comment || selectedGroup.description}
              </div>
            </div>
          ) : null}
        </>
      ) : !selectedItem ? (
        <OfferCalculationSummaryPanel summary={calculation?.summary} />
      ) : (
        <>
          <div className="offer-calc-context__headline">
            <div className="offer-calc-context__line-title">{title}</div>
            <div className="offer-calc-context__line-meta">
              {posLabel} · {typeLabel}
            </div>
          </div>

          <button
            type="button"
            className="offer-calc-context__edit-btn"
            onClick={() => {
              /* MVP: selection already opens the inline editor in the center pane */
              const el = document.querySelector(
                `[data-offer-calc-row="${selectedItem.row_order ?? selectedIndex}"]`
              );
              if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ block: "nearest", behavior: "smooth" });
              }
            }}
          >
            ✎ {t("offer.calculation.workspace.edit_line")}
          </button>

          <dl className="offer-calc-context__props">
            <div>
              <dt>{t("offer.calculation.workspace.field.quantity")}</dt>
              <dd className="tabular-nums">
                {qty != null ? qty : "—"}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.cost_unit")}</dt>
              <dd className="tabular-nums">
                {unitCost != null && unitCost > 0
                  ? priceFormatter.format(unitCost)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.markup")}</dt>
              <dd className="tabular-nums">
                {surcharge != null && !Number.isNaN(surcharge)
                  ? `${surcharge} %`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.sales_unit")}</dt>
              <dd className="tabular-nums">
                {unitSales != null && unitSales > 0
                  ? priceFormatter.format(unitSales)
                  : "—"}
              </dd>
            </div>
            <div className="offer-calc-context__props-divider" aria-hidden />
            <div>
              <dt>{t("offer.calculation.workspace.field.cost")}</dt>
              <dd className="tabular-nums">
                {lineCost != null
                  ? priceFormatter.format(lineCost)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.total")}</dt>
              <dd className="tabular-nums offer-calc-context__props-strong">
                {formatOrDash(salesAmount, (v) => priceFormatter.format(v))}
                {lineSales?.estimate ? (
                  <span className="offer-calc-context__estimate">
                    {" "}
                    ({t("offer.calculation.totals.estimate_hint")})
                  </span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{t("offer.calculation.workspace.field.margin")}</dt>
              <dd
                className={[
                  "tabular-nums",
                  dg != null && dg >= 0 ? "is-positive" : "",
                  dg != null && dg < 0 ? "is-negative" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {dg != null ? `${dg.toFixed(1)} %` : "—"}
              </dd>
            </div>
            {selectedItem.hours != null && Number(selectedItem.hours) > 0 ? (
              <div>
                <dt>{t("offer.calculation.workspace.field.hours")}</dt>
                <dd className="tabular-nums">{selectedItem.hours}</dd>
              </div>
            ) : null}
          </dl>

          {hasOverride ? (
            <div className="offer-calc-context__card offer-calc-context__card--override">
              <div className="offer-calc-context__card-title">
                {t("offer.calculation.workspace.override_title")}
              </div>
              <div className="offer-calc-context__card-body">
                {selectedItem.override_reason
                  ? String(selectedItem.override_reason)
                  : t("offer.calculation.workspace.override_hint")}
              </div>
            </div>
          ) : null}

          {missingCost ? (
            <div className="offer-calc-context__card offer-calc-context__card--warn">
              <div className="offer-calc-context__card-title">
                ⚠ {t("offer.calculation.workspace.validation_title")}
              </div>
              <div className="offer-calc-context__card-body">
                {t("offer.calculation.workspace.missing_cost_line")}
              </div>
            </div>
          ) : null}

          <div className="offer-calc-context__source">
            {t("offer.calculation.workspace.source")}:{" "}
            {selectedItem.price_source || "—"}
          </div>
        </>
      )}
    </aside>
  );
}
